/**
 * The idle drawer (P2.12-T32, ADR-100's note under decision 42): the Region picture is drawn ahead, a few chunks in
 * each idle callback and never inside a frame's budget, so the first zoom out finds it drawn. The idle scheduling comes
 * in as a port, so the rule is tested by hand; the page's own is `frameIdleScheduler`.
 */

/**
 * The most chunks drawn in one idle callback.
 */
export const IDLE_CHUNKS_MOST = 4;

/**
 * The least time, in milliseconds, an idle callback must have left to draw another chunk.
 */
export const IDLE_MIN_MS = 4;

/**
 * How many callbacks in a row may leave a chunk undrawn for want of time before the next one draws a chunk whatever
 * time it has left. A chunk dearer than a callback can ever offer would otherwise never be drawn, and the picture
 * would stay unfinished; at one callback a frame this costs one long frame in `IDLE_STARVED_MOST` plus one.
 */
export const IDLE_STARVED_MOST = 30;

/**
 * What an idle callback is told: whether it ran because its time ran out, and how long it has left.
 */
export interface IdleDeadline {
  readonly didTimeout: boolean;
  timeRemaining(): number;
}

/**
 * The browser's idle scheduling: ask to be called when idle, and take a request back.
 */
export interface IdleScheduler {
  request(callback: (deadline: IdleDeadline) => void): number;
  cancel(id: number): void;
}

/**
 * What the drawer draws: one chunk a call.
 */
export interface IdleTarget {
  prefetch(): boolean;
}

/**
 * A running drawer.
 */
export interface IdleDrawer {
  stop(): void;
}

/**
 * Starts drawing a target ahead. The first request is made at once; each callback draws chunks while it has at least
 * the floor left, up to `IDLE_CHUNKS_MOST`, and asks again while the target says more are owed. The floor is
 * `IDLE_MIN_MS` or the longest chunk cost seen so far, whichever is more, so a chunk is not begun with less time left
 * than the slowest has taken, except that after `IDLE_STARVED_MOST` callbacks in a row that drew nothing the next
 * draws one chunk whatever time it has left. A target that throws
 * ends the drawer and the error goes out of the callback.
 *
 * @param target What to draw; `prefetch` draws one chunk and tells whether more are owed.
 * @param scheduler The idle scheduling.
 * @param now The clock in milliseconds, the page's by default.
 * @returns The drawer, which `stop` ends.
 * @throws Error whatever `target.prefetch` throws, from inside the idle callback.
 */
export function createIdleDrawer(target: IdleTarget, scheduler: IdleScheduler, now: () => number = () => performance.now()): IdleDrawer {
  let floor = IDLE_MIN_MS;
  let id: number | null = null;
  let stopped = false;
  let starved = 0;
  const ask = (): void => {
    id = scheduler.request((deadline) => {
      id = null;
      if (stopped) {
        return;
      }
      let more = true;
      let drawn = 0;
      try {
        while (more && drawn < IDLE_CHUNKS_MOST && (deadline.timeRemaining() >= floor || (drawn === 0 && starved >= IDLE_STARVED_MOST))) {
          const began = now();
          more = target.prefetch();
          floor = Math.max(floor, now() - began);
          drawn += 1;
        }
      } catch (error) {
        stopped = true;
        throw error;
      }
      starved = drawn > 0 ? 0 : starved + 1;
      if (more) {
        ask();
      }
    });
  };
  ask();
  return {
    stop: () => {
      stopped = true;
      if (id !== null) {
        scheduler.cancel(id);
        id = null;
      }
    },
  };
}

/**
 * The frame, in milliseconds, an idle callback measures its time against: the longest a frame may take at 60 frames a
 * second.
 */
export const IDLE_FRAME_MS = 16;

/**
 * The timer's delay, in milliseconds, between a frame's start and the callback that follows it.
 */
export const IDLE_YIELD_MS = 2;

/**
 * What the frame scheduler needs of the page: the frame and the timer, and the clock.
 */
export interface FrameHost {
  requestAnimationFrame(callback: (time: number) => void): number;
  cancelAnimationFrame(id: number): void;
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(id: number): void;
  readonly performance: { now(): number };
}

/**
 * The idle scheduler of a page whose browsers may have no `requestIdleCallback` (Safari, which is the floor, has none,
 * and `scripts/boundaries/browser-floor.ts` refuses it): a callback runs on a timer started from a frame, which fires
 * after that frame is painted, and is told the time left of the frame it began in. A frame that has used its 16 ms
 * leaves nothing, so nothing is drawn and the next frame is asked for; a page that is not drawing frames draws nothing.
 *
 * @param host The page's window, or whatever carries the frame, the timer and the clock.
 * @returns The scheduler.
 */
export function frameIdleScheduler(host: FrameHost): IdleScheduler {
  let next = 1;
  const live = new Map<number, { frame: number | null; timer: number | null }>();
  return {
    request: (callback) => {
      const id = next;
      next += 1;
      const entry: { frame: number | null; timer: number | null } = { frame: null, timer: null };
      live.set(id, entry);
      entry.frame = host.requestAnimationFrame((time) => {
        entry.frame = null;
        entry.timer = host.setTimeout(() => {
          entry.timer = null;
          live.delete(id);
          callback({ didTimeout: false, timeRemaining: () => Math.max(0, IDLE_FRAME_MS - (host.performance.now() - time)) });
        }, IDLE_YIELD_MS);
      });
      return id;
    },
    cancel: (id) => {
      const entry = live.get(id);
      if (entry === undefined) {
        return;
      }
      live.delete(id);
      if (entry.frame !== null) {
        host.cancelAnimationFrame(entry.frame);
      }
      if (entry.timer !== null) {
        host.clearTimeout(entry.timer);
      }
    },
  };
}
